import { useMemo } from 'react';
import { useTextContent } from './useTextContent';
import { InlineStatus } from './InlineStatus';

interface Props {
  contentUrl: string;
}

interface Parsed {
  pretty: string;
  parseError: string | null;
}

// Pretty-printed JSON with an invalid-JSON fallback so corrupted files
// still render (as raw text with a warning banner) instead of showing
// "error" and leaving the user nothing to see.
function prettify(text: string): Parsed {
  try {
    const parsed = JSON.parse(text) as unknown;
    return { pretty: JSON.stringify(parsed, null, 2), parseError: null };
  } catch (err) {
    return { pretty: text, parseError: (err as Error).message };
  }
}

export function JsonView({ contentUrl }: Props) {
  const content = useTextContent(contentUrl);
  const parsed = useMemo<Parsed | null>(
    () => (content.status === 'ready' ? prettify(content.text) : null),
    [content],
  );

  if (content.status === 'loading') return <InlineStatus label="Loading JSON…" />;
  if (content.status === 'error') return <InlineStatus label={`Error: ${content.message}`} error />;
  if (content.status === 'too-large')
    return <InlineStatus label="File is too large to render — use Download." error />;
  if (!parsed) return <InlineStatus label="Parsing JSON…" />;

  return (
    <div className="mx-auto max-w-5xl">
      {parsed.parseError ? (
        <div
          className="mb-3 rounded border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
          role="alert"
        >
          Invalid JSON: {parsed.parseError}. Showing raw file contents.
        </div>
      ) : null}
      <pre className="overflow-auto rounded-lg bg-panel p-4 font-mono text-sm text-panelFg">
        {parsed.pretty}
      </pre>
    </div>
  );
}
